import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api";
import { Button, Field, Modal, inr } from "../components/UI";
import { ArrowLeft, Download, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "cash", note: "" });

  const load = async () => {
    try {
      const [i, p] = await Promise.all([api.get(`/invoices/${id}`), api.get(`/payments?invoice_id=${id}`)]);
      setInv(i.data); setPayments(p.data);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/invoices");
    }
  };
  useEffect(() => { load(); }, [id]);

  const addPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { invoice_id: id, amount: Number(form.amount), method: form.method, note: form.note });
      toast.success("Payment recorded");
      setOpen(false); setForm({ amount: "", method: "cash", note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!inv) return <div className="p-8 text-[#475569]">Loading…</div>;
  const due = inv.total - inv.paid_amount;

  return (
    <div className="p-4 lg:p-8 max-w-5xl">
      <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#C1121F] mb-4" data-testid="back-to-invoices">
        <ArrowLeft size={14}/> Back to Invoices
      </Link>

      <div className="card-flat p-8" data-testid="invoice-detail">
        <div className="flex items-start justify-between pb-6 border-b border-[#E5E7EB]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-[#C1121F] font-semibold">Singh Offset</div>
            <h1 className="font-display text-3xl font-bold mt-1">Invoice {inv.invoice_no}</h1>
            <div className="text-sm text-[#475569] mt-1">Issued {inv.created_at.slice(0, 10)}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={downloadPdf} data-testid="download-pdf-btn"><Download size={15}/> Download PDF</Button>
            {due > 0 && (
              <Button onClick={() => { setForm({ amount: due.toFixed(2), method: "cash", note: "" }); setOpen(true); }} data-testid="add-payment-btn">
                <Plus size={15}/> Record Payment
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 py-6 border-b border-[#E5E7EB]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Bill To</div>
            <div className="font-display font-bold text-lg mt-1">{inv.customer_name}</div>
            <div className="text-sm text-[#475569] mt-0.5">{inv.customer_address}</div>
            {inv.customer_gstin && <div className="text-xs font-mono-num text-[#475569] mt-1">GSTIN: {inv.customer_gstin}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Balance Due</div>
            <div className="font-mono-num font-bold text-3xl text-[#C1121F] mt-1">{inr(due)}</div>
            <div className="text-xs text-[#475569] mt-0.5">{inv.payment_status.toUpperCase()}</div>
          </div>
        </div>

        <table className="crm-table mt-6">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit Price</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, idx) => (
              <tr key={idx}>
                <td className="font-semibold">{it.product_name}</td>
                <td className="text-right font-mono-num">{it.quantity}</td>
                <td className="text-right font-mono-num">{inr(it.unit_price)}</td>
                <td className="text-right font-mono-num font-semibold">{inr(it.quantity * it.unit_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-4">
          <div className="w-72 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#475569]">Subtotal</span><span className="font-mono-num">{inr(inv.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#475569]">Tax ({inv.tax_percent}%)</span><span className="font-mono-num">{inr(inv.tax)}</span></div>
            <div className="flex justify-between text-base font-bold pt-2 border-t border-[#E5E7EB]"><span>Total</span><span className="font-mono-num text-[#C1121F]">{inr(inv.total)}</span></div>
            <div className="flex justify-between text-green-700"><span>Paid</span><span className="font-mono-num">{inr(inv.paid_amount)}</span></div>
            <div className="flex justify-between font-bold text-[#C1121F] pt-2 border-t border-[#E5E7EB]"><span>Due</span><span className="font-mono-num">{inr(due)}</span></div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
          <div className="flex items-center gap-2 mb-3"><Receipt size={16} className="text-[#C1121F]"/><h3 className="font-display font-bold">Payment History</h3></div>
          {payments.length === 0 ? (
            <div className="text-sm text-[#94A3B8] py-3">No payments yet.</div>
          ) : (
            <table className="crm-table">
              <thead><tr><th>Date</th><th>Method</th><th>Note</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="text-xs text-[#475569]">{p.created_at.slice(0, 10)}</td>
                    <td className="capitalize">{p.method.replace("_", " ")}</td>
                    <td className="text-[#475569]">{p.note || "—"}</td>
                    <td className="text-right font-mono-num font-semibold text-green-700">{inr(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Record Payment">
        <form onSubmit={addPayment} className="space-y-4">
          <Field label="Amount (₹)" required>
            <input data-testid="payment-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="input-base" />
          </Field>
          <Field label="Method" required>
            <select data-testid="payment-method" value={form.method} onChange={(e) => setForm({...form, method: e.target.value})} className="input-base">
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Note">
            <input data-testid="payment-note" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} className="input-base" placeholder="UTR / cheque no" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-payment-btn">Record Payment</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
